import React from 'react';

import { Inject } from '@odinjs/odin';

import { Endpoint, ToastServiceProviderAccreditation } from '@philips-emr/hpms-provider-accreditation-commons';
import { FetchAPI, FieldAPI, FieldListAPI, FormAPI, Link, PanelAPI } from '@philips-emr/tws-components';
import { Size } from '@philips-emr/tws-constants';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnAfterFetch, OnCancel, OnClose, OnDynamicContentRender, OnMount, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { FetchEvent, MountEvent, RenderEvent, SubmitEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService, ToastService } from '@philips-emr/tws-core/lib/services';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { FragmentNames } from '../../codegen';
import { NegotiationPackagesAliases as Aliases } from '../../codegen/constants/aliases/negotiation-packages-aliases';
import BaseController from '../BaseController';

import './negotiation-packages.css';

interface Item {
  dsItem: string,
  dsType: string,
  amount: number,
}

interface Package {
  dsItem: string,
  edit: string,
  vlPackage: number,
  nrSeqItemProposal: number,
  vlCpPackage: number,
  ieValueCounterProposal: string,
  packageCompositionItem: Item[],
}

const PACKAGE = 'PCT';
const IS_CHANGE_VALUE = 'S';

@Controller({ fragment: FragmentNames.NEGOTIATION_PACKAGES })
class NegotiationPackagesController extends BaseController {

  @Inject(Aliases.PANEL_DETAILS)
  private panelModalPackage: PanelAPI;
  @Inject(Aliases.FETCH_PACKAGES)
  private fetchPackages: FetchAPI;
  @Inject(Aliases.FIELD_PACKAGE_VALUE_DETAILS)
  private fieldPackageValueDetails: FieldAPI;
  @Inject(Aliases.FIELD_PACKAGE_VALUE_ORIGIN)
  private fieldPackageValueOrigin: FieldAPI;
  @Inject(Aliases.FIELD_COUNTER_OFFER_VALUE_DETAIL)
  private fieldCounterOfferValueDetail: FieldAPI;
  @Inject(Aliases.HPMS_CUSTOM_LIST_FIELD)
  private hpmsCustomListField: FieldListAPI;
  @Inject(Aliases.FORM_PACKAGE)
  private formPackage: FormAPI;
  @Inject(Aliases.PANEL_COMPOSITION_TITLE)
  private panelCompositionTitle: PanelAPI;
  @Inject(Aliases.PANEL_LIST_FIELD_ITENS)
  private panelListFieldItens: PanelAPI;
  @Inject(Aliases.PANEL_LOADER)
  private panelLoader: PanelAPI;

  @OnMount(Aliases.FETCH_PACKAGES)
  onMountFetchPackages(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const { nrSeqProposal, nrSeqNegotiation } = this.historyService.getCurrentRouteParams();
    this.context.set({ nrSeqNegotiation, nrSeqProposal, 'itemType': PACKAGE });
    api.setParameters({ nrSeqNegotiation, nrSeqProposal, 'itemType': PACKAGE });
    api.forceFetch();
  }

  @OnMount(Aliases.FIELD_COUNTER_OFFER_VALUE)
  onMountFieldNewValue(event: MountEvent): void {
    const api = event.target as FieldAPI;
    const data = api.getData();
    if (data) {
      const { vlCpPackage } = data as unknown as Package;
      if (vlCpPackage > 0) {
        api.setVisible(true);
      }
    }
  }

  @OnDynamicContentRender(Aliases.CARD_PACKAGE)
  onRenderLinkEdit(event: RenderEvent): void {
    const { render, data } = event;
    const { edit, nrSeqItemProposal } = data as Package;
    if (!isEmptyArrayOrObject(data)) {
      render(() =>
        <Link
          onClick={() => this.onDetail(nrSeqItemProposal)}
          value={edit === IS_CHANGE_VALUE ? MessageService.translate('pa.change-value') : MessageService.translate('pa.details')}
          size={Size.MEDIUM}
        />
      );
    }
  }

  private onDetail(nrSeqItemProposal: number): void {
    this.context.set({ nrSeqItemProposal });
    this.panelModalPackage.setVisible(true);
  }

  @OnSubmit(Aliases.FORM_PACKAGE)
  onSubmitFormPackage(event: SubmitEvent): void {
    const body = { ...event.record, 'itemType': PACKAGE };
    const { nrSeqProposal, nrSeqNegotiation } = this.historyService.getCurrentRouteParams();
    HttpService.post(Endpoint.POST.EDIT_NEGOTIATION_PROPOSAL_ITEM, { nrSeqProposal, nrSeqNegotiation }, { body })
      .then(() => {
        this.panelModalPackage.setVisible(false);
        this.fetchPackages.forceFetch();
        ToastServiceProviderAccreditation.successBubble(MessageService.translate('pa.request-success'));
      }).catch(() => {
        ToastServiceProviderAccreditation.error(MessageService.translate('pa.error-process-request'));
      });
  }

  @OnAfterFetch(Aliases.FETCH_PACKAGE_DETAILS)
  onAfterFetchPackageDetails(event: FetchEvent): void {
    const data = event.data as Package;
    if (data) {
      const { edit, vlCpPackage, packageCompositionItem } = data;
      this.fieldPackageValueDetails.setVisible(edit === IS_CHANGE_VALUE);
      this.formPackage.setState({ editing: edit === IS_CHANGE_VALUE });
      this.fieldPackageValueOrigin.setVisible(edit != IS_CHANGE_VALUE);
      this.fieldCounterOfferValueDetail.setVisible(edit != IS_CHANGE_VALUE && vlCpPackage > 0);
      if (!isEmptyArrayOrObject(packageCompositionItem) && packageCompositionItem.length > 0) {
        this.getPackageItems();
      }
    }
  }

  private getPackageItems(): void {
    this.panelLoader.setVisible(true);
    const { nrSeqNegotiation, nrSeqProposal, itemType, nrSeqItemProposal } = this.context.getBulkData();
    HttpService.get<Package>(Endpoint.GET.PACKAGE_COMPOSITION_ITEMS, { nrSeqNegotiation, nrSeqProposal, itemType, nrSeqItemProposal })
      .then(({ body }) => {
        if (body) {
          this.panelLoader.setVisible(false);
          this.panelListFieldItens.setVisible(true);
          this.panelCompositionTitle.setVisible(true);
          const { packageCompositionItem } = body;
          this.hpmsCustomListField.setValue(packageCompositionItem as unknown as Record<string, Value>[]);
        }
      }).catch((response) => {
        this.panelLoader.setVisible(false);
        ToastService.open(response);
      });
  }

  @OnClose(Aliases.MODAL_PACKAGE_DETAILS)
  onCloseModalPackageDetails(): void {
    this.panelModalPackage.setVisible(false);
  }

  @OnCancel(Aliases.FORM_PACKAGE)
  onCancelFormPackage(): void {
    this.panelModalPackage.setVisible(false);
  }
}

export {
  NegotiationPackagesController,
};
